import {
  BookOutlined,
  CheckOutlined,
  CloseOutlined,
  MoreOutlined,
  PrinterOutlined,
  RollbackOutlined,
  ShoppingOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import {
  DeleteButtonProps,
  EditButtonProps,
  ListButtonProps,
  RefreshButton,
  RefreshButtonProps,
} from '@refinedev/antd';
import {
  Button,
  ButtonProps,
  DatePicker,
  Flex,
  Form,
  Input,
  message,
  Modal,
  notification,
  Tooltip,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ReactNode, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import PopupMenu from '@/components/general/PopupMenu';
import PackageMessages from '@/components/logistic/PackageMessages/PackageMessages';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUiContext } from '@/contexts/UiContext';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { SHIPMENT_TYPE } from '@/model/Logistic/Shipment/ShipmentType';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

interface ShipmentShowHeaderButtonsProps {
  record: Shipment;
  refineButtonsProps: {
    editButtonProps: EditButtonProps | undefined;
    deleteButtonProps: DeleteButtonProps | undefined;
    refreshButtonProps: RefreshButtonProps | undefined;
    listButtonProps: ListButtonProps | undefined;
  };
  refetch: () => void;
  blockChangeState: { id: string; message: string }[];
  canSend?: boolean;
  isPaid?: boolean;
  showQuantityAlert?: boolean;
}

const ShipmentShowHeaderButtons = (props: ShipmentShowHeaderButtonsProps) => {
  const { isMediumScreen } = useUiContext();
  const { packageRepository, shipmentRepository } = useRepositoriesContext();
  const { hasPermission } = usePermissionContext();
  const { userWorkArea } = useUserContext();
  const { t } = useTranslation();
  const {
    record,
    refineButtonsProps,
    refetch,
    blockChangeState,
    canSend,
    isPaid,
    showQuantityAlert,
  } = props;
  const { editButtonProps, deleteButtonProps, refreshButtonProps } = refineButtonsProps;
  const { id, status, has_sale_items, external_document, tracking_number, tipo } = record;
  const disableStateButtons = useMemo(() => blockChangeState.length > 0, [blockChangeState]);
  const shipmentDateRef = useRef<Dayjs | null>(null);
  const shipmentTrackingNumberRef = useRef<string>('');
  const cancelationMotiveRef = useRef<string>('');
  const [modal, contextHolder] = Modal.useModal();
  const [api, notificationContextHolder] = notification.useNotification();
  const changeStateButtonsProps: ButtonProps[] = useMemo(() => {
    const checkQuantity = stateAction =>
      showQuantityAlert
        ? modal.confirm({
            title: t('Algunos elementos no tienen cantidad especificada'),
            content: t('¿Desea continuar de todos modos?'),
            okText: t('Sí'),
            okType: 'primary',
            cancelText: t('No'),
            onOk() {
              stateAction();
            },
          })
        : stateAction();

    if (status == PACKAGE_STATES.GESTION_PAGO && userWorkArea == USER_WORK_AREA.ADMINISTRATION) {
      return [
        {
          icon: <ShoppingOutlined />,
          onClick: () => {
            modal.confirm({
              title: t('¿Estás seguro de que deseas marcar como pagados los elementos?'),
              content: t('Esta acción no se puede deshacer'),
              okText: t('Sí'),
              okType: 'primary',
              cancelText: t('No'),
              onOk() {
                void shipmentRepository.updateShipmentState(id, PACKAGE_STATES.PAGADO).then(() => {
                  void refetch();
                });
              },
            });
          },
          children: t('Marcar como pagados'),
          disabled: !isPaid,
        },
      ];
    }

    if (
      status == PACKAGE_STATES.EN_VIAJE &&
      (userWorkArea == USER_WORK_AREA.LOGISTICS || userWorkArea == USER_WORK_AREA.CUSTOMER_SERVICE)
    )
      return [
        {
          icon: <CheckOutlined />,
          onClick: () => {
            shipmentDateRef.current = null;
            const changeStateAction = () =>
              modal.confirm({
                title: t('¿Estás seguro de que deseas marcar como finalizado este envío?'),
                content: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {t('Esta acción no se puede deshacer')}
                    <b>{t('Fecha de recepción del cliente')}:</b>
                    <DatePicker
                      format={'L'}
                      placeholder={t('Seleccionar una fecha')}
                      onChange={date => {
                        shipmentDateRef.current = date;
                      }}
                      maxDate={dayjs()}
                    />
                  </div>
                ),
                okText: t('Sí'),
                okType: 'primary',
                cancelText: t('No'),
                onOk() {
                  const pickedDate = shipmentDateRef.current;

                  if (!pickedDate) {
                    message.error(t('Debe indicar una fecha de envío'));

                    return;
                  }

                  void shipmentRepository
                    .updateShipment(id, {
                      status: PACKAGE_STATES.FINALIZADO,
                      date_finished: pickedDate.toISOString(),
                    })
                    .then(() => {
                      void refetch();
                    });
                },
              });

            checkQuantity(changeStateAction);
          },
          children: t('Marcar recibido'),
        },
      ];

    if (userWorkArea !== USER_WORK_AREA.LOGISTICS) return [];

    if (has_sale_items && status == PACKAGE_STATES.PENDIENTE) {
      return [
        {
          icon: <ShoppingOutlined />,
          onClick: () => {
            modal.confirm({
              title: t('¿Estás seguro de que deseas pasar a gestionar el pago de los elementos?'),
              content: t('Esta acción no se puede deshacer'),
              okText: t('Sí'),
              okType: 'primary',
              cancelText: t('No'),
              onOk() {
                void shipmentRepository
                  .updateShipmentState(id, PACKAGE_STATES.GESTION_PAGO)
                  .then(() => {
                    void refetch();
                  });
              },
            });
          },
          children: t('Pasar a gestión de pago'),
          disabled: !external_document,
        },
      ];
    }

    return status === PACKAGE_STATES.REPARACION ||
      status === PACKAGE_STATES.PENDIENTE ||
      status === PACKAGE_STATES.PAGADO
      ? [
          {
            icon: <ShoppingOutlined />,
            onClick: () => {
              modal.confirm({
                title: t('¿Estás seguro de que deseas preparar este envío?'),
                content: t('Esta acción no se puede deshacer'),
                okText: t('Sí, preparar'),
                okType: 'primary',
                cancelText: t('No'),
                onOk() {
                  void shipmentRepository
                    .updateShipmentState(id, PACKAGE_STATES.PREPARACION)
                    .then(() => {
                      void refetch();
                    });
                },
              });
            },
            children: t('Comenzar preparación'),
          },
        ]
      : status == PACKAGE_STATES.PREPARACION
        ? [
            tipo != SHIPMENT_TYPE.AUTOMATICO
              ? {
                  icon: <RollbackOutlined />,
                  onClick: () => {
                    modal.confirm({
                      title: t('¿Estás seguro de que deseas volver esta paquete a pendiente?'),
                      okText: t('Sí'),
                      okType: 'primary',
                      cancelText: t('No'),
                      onOk() {
                        void shipmentRepository
                          .updateShipmentState(id, PACKAGE_STATES.PENDIENTE)
                          .then(() => {
                            void refetch();
                          });
                      },
                    });
                  },
                  type: 'default',
                  children: t('Volver a pendiente'),
                  style: has_sale_items ? { display: 'none' } : {},
                }
              : { style: { display: 'none' } },

            {
              icon: <BookOutlined />,
              onClick: () => {
                shipmentDateRef.current = null;
                modal.confirm({
                  title: t('¿Estás seguro de que deseas marcar este paquete como preparado?'),
                  okText: t('Sí'),
                  okType: 'primary',
                  cancelText: t('No'),
                  onOk() {
                    void shipmentRepository
                      .updateShipmentState(id, PACKAGE_STATES.PREPARADO)
                      .then(() => {
                        void refetch();
                      });
                  },
                });
              },
              children: t('Finalizar preparación'),
              disabled: !canSend,
            },
          ]
        : status == PACKAGE_STATES.PREPARADO
          ? [
              tipo != SHIPMENT_TYPE.AUTOMATICO
                ? {
                    icon: <RollbackOutlined />,
                    onClick: () => {
                      modal.confirm({
                        title: t('¿Estás seguro de que deseas volver esta paquete a pendiente?'),
                        content: t('Esta acción no se puede deshacer'),
                        okText: t('Sí'),
                        okType: 'primary',
                        cancelText: t('No'),
                        onOk() {
                          void shipmentRepository
                            .updateShipmentState(id, PACKAGE_STATES.PENDIENTE)
                            .then(() => {
                              void refetch();
                            });
                        },
                      });
                    },
                    type: 'default',
                    children: t('Volver a pendiente'),
                    style: has_sale_items ? { display: 'none' } : {},
                  }
                : { style: { display: 'none' } },

              {
                icon: <TruckOutlined />,
                onClick: () => {
                  shipmentDateRef.current = null;
                  shipmentTrackingNumberRef.current = tracking_number || '';
                  const changeStateAction = () =>
                    modal.confirm({
                      title: t('¿Estás seguro de que deseas marcar como enviado?'),
                      content: (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                          {t('Esta acción no se puede deshacer')}
                          <b>{t('Fecha de envío')}:</b>
                          <DatePicker
                            format={'L'}
                            placeholder={t('Seleccionar una fecha')}
                            onChange={date => {
                              shipmentDateRef.current = date;
                            }}
                            maxDate={dayjs()}
                          />
                          <b>{t('Número de seguimiento')}:</b>
                          <Input
                            placeholder={t('Introducir número de seguimiento')}
                            onChange={v => {
                              shipmentTrackingNumberRef.current = v.target.value;
                            }}
                            defaultValue={shipmentTrackingNumberRef.current}
                          />
                        </div>
                      ),
                      okText: t('Sí'),
                      okType: 'primary',
                      cancelText: t('No'),
                      onOk() {
                        const pickedDate = shipmentDateRef.current;

                        if (!pickedDate) {
                          message.error(t('Debe indicar una fecha de envío'));

                          return;
                        }

                        void shipmentRepository
                          .updateShipment(id, {
                            status: PACKAGE_STATES.EN_VIAJE,
                            date: pickedDate.toISOString(),
                            tracking_number: shipmentTrackingNumberRef.current ?? undefined,
                          })
                          .then(data => {
                            if (data.reception_created) {
                              api.success({
                                message: t('Se ha creado la recepción asociada al envío'),
                                description: t(
                                  'Haga click para abrir la recepción en un nuevo enlace',
                                ),
                                onClick: () => {
                                  window.open(
                                    `/v2/receptions/show/${data.reception_created}`,
                                    '_blank',
                                  );
                                },
                              });
                            } else if (data.service_order_is_created) {
                              api.success({
                                message: t('Se ha creado una orden de servicio asociada al envío'),
                                description: t(
                                  'Haga click para abrir la orden de servicio en un nuevo enlace',
                                ),
                                onClick: () => {
                                  window.open(
                                    `/v2/${RESOURCES.SERVICE_ORDERS}/show/${data.service_order}`,
                                    '_blank',
                                  );
                                },
                              });
                            } else if (data.service_order_is_created === false) {
                              api.success({
                                message: t(
                                  'Se ha actualizado una orden de servicio pendiente de coordinación',
                                ),
                                description: t(
                                  'Haga click para abrir la orden de servicio en un nuevo enlace',
                                ),
                                onClick: () => {
                                  window.open(
                                    `/v2/${RESOURCES.SERVICE_ORDERS}/show/${data.service_order}`,
                                    '_blank',
                                  );
                                },
                              });
                            }

                            void refetch();
                          });
                      },
                    });

                  checkQuantity(changeStateAction);
                },
                children: t('Enviar'),
                disabled: !canSend,
              },
            ]
          : [];
  }, [
    status,
    userWorkArea,
    has_sale_items,
    t,
    canSend,
    isPaid,
    showQuantityAlert,
    modal,
    shipmentRepository,
    id,
    refetch,
    external_document,
    tipo,
    tracking_number,
    api,
  ]);
  const changeStateBlockTooltip = useMemo(
    () =>
      blockChangeState?.length > 0
        ? `${t('No se puede realizar esta acción')}:\n${blockChangeState.map(bs => bs.message).join('\n')}`
        : status == PACKAGE_STATES.PENDIENTE && has_sale_items && !external_document
          ? t('Para proceder a la gestión de pago debe agregar una orden de venta')
          : status == PACKAGE_STATES.GESTION_PAGO && has_sale_items && !isPaid
            ? t('Para proceder a la preparación debe marcar los elementos como pagados')
            : status == PACKAGE_STATES.PREPARACION && !canSend
              ? t('Faltan especificar números de serie o unidades en los elementos del envío')
              : '',
    [blockChangeState, canSend, external_document, has_sale_items, isPaid, status, t],
  );
  const changeStateButton = useMemo(
    () =>
      changeStateButtonsProps.length > 0
        ? changeStateButtonsProps.map((props: ButtonProps, index) => (
            <Tooltip key={index} title={changeStateBlockTooltip || ''}>
              <Button
                key={'change-state-' + index}
                type={'primary'}
                color={isMediumScreen ? 'primary' : undefined}
                {...props}
                disabled={disableStateButtons || props.disabled}
              />
            </Tooltip>
          ))
        : null,
    [changeStateBlockTooltip, changeStateButtonsProps, disableStateButtons, isMediumScreen],
  );
  const cancelButton = useMemo(
    () =>
      (userWorkArea == USER_WORK_AREA.ADMINISTRATION && status == PACKAGE_STATES.GESTION_PAGO) ||
      (hasPermission('edit') &&
        (status == PACKAGE_STATES.PENDIENTE ||
          (userWorkArea == USER_WORK_AREA.LOGISTICS &&
            (status == PACKAGE_STATES.PREPARACION ||
              status == PACKAGE_STATES.PREPARADO ||
              status == PACKAGE_STATES.REPARACION)))) ? (
        <Tooltip title={changeStateBlockTooltip}>
          <Button
            key={'cancel'}
            disabled={disableStateButtons}
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              cancelationMotiveRef.current = '';

              modal.confirm({
                title: t('¿Estás seguro de que deseas cancelar este envío?'),

                content: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {t('Esta acción no se puede deshacer')}
                    <Form.Item layout="vertical" label={t('Motivo de cancelación')} required>
                      <Input
                        placeholder={t('Motivo de cancelación')}
                        onChange={e => {
                          cancelationMotiveRef.current = e.target.value;
                        }}
                      />
                    </Form.Item>
                  </div>
                ),
                okText: t('Sí, cancelar'),
                okType: 'danger',
                cancelText: t('No'),
                onOk() {
                  const cancelationMotive = cancelationMotiveRef.current;

                  if (!cancelationMotive || cancelationMotive.trim() === '') {
                    message.error(t('Debe indicar un motivo de cancelación'));

                    return;
                  }

                  void shipmentRepository
                    .updateShipment(id, {
                      status: PACKAGE_STATES.CANCELADO,
                      cancellation_motive: cancelationMotive,
                    })
                    .then(() => {
                      void refetch();
                    });
                },
              });
            }}
            type={isMediumScreen ? 'text' : 'default'}>
            {t('Cancelar')}
          </Button>
        </Tooltip>
      ) : null,
    [
      hasPermission,
      status,
      userWorkArea,
      changeStateBlockTooltip,
      disableStateButtons,
      isMediumScreen,
      modal,
      shipmentRepository,
      id,
      refetch,
      t,
    ],
  );
  const popupMenuContent: ReactNode[] = useMemo(() => {
    const buttons: ReactNode[] = [];

    if (isMediumScreen && changeStateButton) {
      buttons.push(changeStateButton);
    }

    if (
      status == PACKAGE_STATES.PENDIENTE ||
      status == PACKAGE_STATES.PREPARACION ||
      status == PACKAGE_STATES.PREPARADO
    ) {
      buttons.push(
        <Button
          key={'print'}
          icon={<PrinterOutlined />}
          type="text"
          onClick={() => id && void packageRepository.printShippingLabel(id, RESOURCES.SHIPMENTS)}>
          {t('Imprimir etiqueta')}
        </Button>,
      );
    }

    if (status == PACKAGE_STATES.PENDIENTE) {
      buttons.push(<ProtectedEditButton key={'edit'} {...editButtonProps} type="text" />);
    }

    if (isMediumScreen && cancelButton) {
      buttons.push(cancelButton);
    }

    if (status == PACKAGE_STATES.PENDIENTE) {
      buttons.push(<ProtectedDeleteButton key={'delete'} {...deleteButtonProps} type="text" />);
    }

    return buttons;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cancelButton,
    changeStateButton,
    deleteButtonProps,
    editButtonProps,
    id,
    isMediumScreen,
    status,
    t,
  ]);

  return (
    <Flex gap={8} wrap align="center" style={{ minHeight: '40px' }}>
      <RefreshButton
        key="refresh"
        {...refreshButtonProps}
        hideText
        size={isMediumScreen ? 'small' : 'middle'}
      />
      {!isMediumScreen && (
        <>
          {cancelButton} {changeStateButton}
        </>
      )}

      {popupMenuContent.length > 0 && (
        <PopupMenu
          openButton={<Button icon={<MoreOutlined />} size={isMediumScreen ? 'small' : 'middle'} />}
          disabled={disableStateButtons}
          tooltip={changeStateBlockTooltip}
          menuContent={
            <Flex vertical align="start" gap={8}>
              {popupMenuContent}
            </Flex>
          }
        />
      )}
      {contextHolder}
      {notificationContextHolder}
      <PackageMessages packageId={id} type="shipment" />
    </Flex>
  );
};

export default ShipmentShowHeaderButtons;
